import { z } from 'zod';
import { PointMm } from '@besbello/design-core';

/**
 * P4 — Las reglas son DATOS VERSIONADOS, no codigo. Cambiar el maximo de tintas
 * de la serigrafia o el dpi minimo del DTF es editar una fila, no desplegar.
 * Estos esquemas son la forma de esa fila.
 */

export const TechniqueId = z.enum(['dtf', 'screen_print', 'embroidery', 'sublimation']);
export type TechniqueId = z.infer<typeof TechniqueId>;

export const ColorSpaceRule = z.enum(['rgb', 'cmyk', 'spot']);

export const TechniqueRuleVersion = z.object({
  techniqueId: TechniqueId,
  /** Version de la regla. Se registra en el presupuesto y en el manifiesto. */
  version: z.string().min(1),
  /** Vigencia; fuera de ella la regla no se aplica. */
  validFrom: z.string().datetime(),
  validUntil: z.string().datetime().nullable(),

  /** Maximo de tintas separables. `null` = sin limite (cuatricromia/digital). */
  maxInks: z.number().int().positive().nullable(),
  /** Resolucion efectiva minima, en dpi REALES sobre el tamano impreso. */
  minEffectiveDpi: z.number().positive(),
  /** Grosor de trazo minimo reproducible, en mm. */
  minStrokeWidthMm: z.number().positive(),
  /** Cuerpo de texto minimo legible, en mm. */
  minTextSizeMm: z.number().positive(),
  /** Area maxima marcable, en mm2. `null` = solo limita la zona. */
  maxAreaMm2: z.number().positive().nullable(),
  /** Numero maximo de capas admitido por el proceso. */
  maxLayers: z.number().int().positive().nullable(),

  /** Tipos de color que el proceso admite en el documento. */
  allowedColorTypes: z.array(z.enum(['rgb', 'cmyk', 'spot', 'technical_white'])),
  /** Degradados: la serigrafia de tinta plana no los admite. */
  gradientsAllowed: z.boolean(),
  /** Si el proceso puede imprimir blanco de subbase. */
  technicalWhiteAvailable: z.boolean(),
  /** Bordado: el paquete es una PETICION DE DIGITALIZACION, no puntadas. */
  requiresExternalDigitizing: z.boolean(),
  /** Soportes admitidos (material de la prenda u objeto). */
  allowedSubstrates: z.array(z.string().min(1)),
});
export type TechniqueRuleVersion = z.infer<typeof TechniqueRuleVersion>;

/** Zona de marcaje EFECTIVA, ya resuelta para producto + color + vista + talla. */
export const EffectiveZone = z.object({
  zoneId: z.string().min(1),
  name: z.string().min(1),
  polygonMm: z.array(PointMm).min(3),
  allowedTechniques: z.array(TechniqueId).min(1),
  /** Procedencia del valor: de donde sale esta zona (Fase 2). */
  provenance: z.enum(['inherited', 'adjusted', 'exception']).default('inherited'),
  confidence: z.number().min(0).max(1).default(1),
});
export type EffectiveZone = z.infer<typeof EffectiveZone>;

export const ValidationContext = z.object({
  zone: EffectiveZone,
  technique: TechniqueRuleVersion,
  /** Material del producto: "algodon 100%", "poliester", "ceramica"... */
  substrate: z.string().min(1),
  /** Instante de referencia, ISO-8601. El motor es PURO: no lee el reloj. */
  now: z.string().datetime(),
});
export type ValidationContext = z.infer<typeof ValidationContext>;

export function ruleIdOf(rule: TechniqueRuleVersion): string {
  return `${rule.techniqueId}@${rule.version}`;
}

/** Una regla solo se aplica dentro de su vigencia. */
export function isRuleInForce(rule: TechniqueRuleVersion, nowIso: string): boolean {
  const now = Date.parse(nowIso);
  if (Number.isNaN(now)) return false;
  if (now < Date.parse(rule.validFrom)) return false;
  if (rule.validUntil !== null && now > Date.parse(rule.validUntil)) return false;
  return true;
}
